import React from 'react';
import { useTranslation } from 'react-i18next';

import ActionButtons from '@/billing/component/ActionButtons';
import { useIsLockedByOtherUser } from '@/billing/hooks/useIsLockedByOtherUser';
import { Badge } from '@/common/components/ui/badge';
import { formatDate } from '@/common/utils/DateFormatters';

import { BillingSummary } from '../api/FetchBillingList';
import { BillingStatusBadge } from './BillingStatusBadge';

interface DetailsTabProps {
  billing: BillingSummary;
  onClose: () => void;
}

export const DetailsTab = ({ billing, onClose }: DetailsTabProps) => {
  const { t, i18n } = useTranslation();
  const isLocked = useIsLockedByOtherUser(billing);

  const isStatusGreyedOut = isLocked && billing.status !== 'COMPLETED';

  const detailsList = [
    {
      label: t('billing.status'),
      content: (
        <BillingStatusBadge
          status={billing.status}
          greyedOut={isStatusGreyedOut}
        />
      ),
    },
    {
      label: t('billing.deversement'),
      content: formatDate(billing.createdAt, i18n.language),
    },
    {
      label: t('billing.searchCode'),
      content: billing.searchCode,
    },
    {
      label: t('billing.insurer'),
      content: billing.insurer,
    },
    {
      label: t('billing.activityCode'),
      content: billing.activityCode,
    },
    {
      label: t('billing.effectiveDate'),
      content: billing.effectiveDate
        ? formatDate(billing.effectiveDate, i18n.language)
        : null,
    },
    {
      label: t('billing.expiryDate'),
      content: billing.expiryDate
        ? formatDate(billing.expiryDate, i18n.language)
        : null,
    },
    {
      label: t('billing.cv1'),
      content: billing.cv1,
      hasTopSeparator: true,
    },
    {
      label: t('billing.gc1'),
      content: billing.gc1,
    },
    {
      label: t('billing.technicalSupport'),
      content: billing.technicalSupport,
    },
    {
      label: t('billing.agencyOrDirectBilling'),
      content: billing.isAgencyBill ? t('billing.agency') : t('billing.direct'),
      hasTopSeparator: true,
    },
    {
      label: t('billing.service'),
      content: <Badge variant="outline">{billing.service}</Badge>,
    },
    {
      label: t('billing.entity'),
      content: <Badge variant="outline">{billing.entity}</Badge>,
    },
  ];

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 px-6">
        <ul className="flex flex-col gap-2">
          {detailsList.map((detail) => (
            <React.Fragment key={detail.label}>
              {detail.hasTopSeparator && <hr className="my-3" />}
              <li className="flex items-center justify-between">
                <span className="text-ring font-semibold text-sm">
                  {detail.label}
                </span>
                <span className="font-medium">{detail.content}</span>
              </li>
            </React.Fragment>
          ))}
        </ul>
      </div>

      {billing.status !== 'COMPLETED' && (
        <ActionButtons billing={billing} onClose={onClose} />
      )}
    </>
  );
};
